<template>
  <ComponentHeader title="Bulk Assign" subtitle="Assign multiple tasks to users based on entity and filter criteria" />

  <div class="p-4">
    <!-- Notifications -->
    <div v-if="notifications && notifications.length > 0" class="mb-4">
      <Reusable_Alert
        v-for="(notification, index) in notifications"
        :key="`notification-${index}`"
        :message="notification.message"
        :color="notification.color"
        @close="removeNotification(index)"
      />
    </div>

    <!-- Search Filters -->
    <div class="">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Search Filters</h3>

      <!-- Required Filter: Task Type -->
      <div class="mb-6">
        <div class="flex items-center gap-2 mb-2">
          <label class="block text-sm font-medium text-gray-700">
            Task Type
          </label>
          <span class="text-xs text-red-500 font-medium">* Required</span>
        </div>
        <select
          v-model="filters.taskType"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-blue-dark transition-colors"
        >
          <option value="">Select a task type...</option>
          <option v-for="type in taskTypes" :key="type.id" :value="type.id">
            {{ type.name }}
          </option>
        </select>
      </div>

      <!-- Optional Filters -->
      <div class="border-t border-gray-200 pt-6">
        <label class="block text-sm font-medium text-gray-700 mb-4">
          Optional Filters
        </label>

        <!-- Entity Selection (Optional) -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-600 mb-2">
            Filter by Entities
          </label>
          <div class="flex items-center gap-4">
            <EntitySearch_V2
              :initial-selection="selectedEntityIds"
              @entities-selected="handleEntitiesSelected"
            />
            <div v-if="selectedEntityIds.length > 0" class="flex items-center gap-2 text-sm text-gray-600">
              <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-green-500" />
              <span>{{ selectedEntityIds.length }} {{ selectedEntityIds.length === 1 ? 'entity' : 'entities' }} selected</span>
              <button
                @click="clearEntitySelection"
                class="ml-2 text-red-600 hover:text-red-700 underline text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Priority Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Top Priority Tasks
            </label>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-600">Top</span>
              <input
                v-model.number="filters.topPriorityCount"
                type="number"
                min="1"
                placeholder="e.g., 50"
                class="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-blue-dark transition-colors"
              />
              <span class="text-sm text-gray-600">highest priority tasks</span>
            </div>
            <p class="text-xs text-gray-500 mt-1">Leave blank to include all priorities</p>
          </div>

          <!-- Payer Name Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Payer Name
            </label>
            <input
              v-model="filters.payerName"
              type="text"
              placeholder="Enter payer name..."
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-blue-dark transition-colors"
            />
          </div>
        </div>
      </div>

      <!-- Search Button -->
      <div class="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center">
        <div class="text-sm text-gray-600">
          <p v-if="!filters.taskType" class="flex items-center gap-2">
            <FontAwesomeIcon :icon="['fas', 'info-circle']" class="text-blue-500" />
            Select a task type to search for tasks
          </p>
          <p v-else-if="selectedEntityIds.length === 0" class="flex items-center gap-2">
            <FontAwesomeIcon :icon="['fas', 'info-circle']" class="text-amber-500" />
            No entities selected - will search across all entities
          </p>
        </div>
        <button
          @click="searchTasks"
          :disabled="isSearching || !filters.taskType"
          class="btn-primary"
        >
          <span v-if="!isSearching" class="flex items-center gap-2">
            <FontAwesomeIcon :icon="['fas', 'search']" />
            Search Tasks
          </span>
          <span v-else class="flex items-center gap-2">
            <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
            Searching...
          </span>
        </button>
      </div>
    </div>

    <!-- Results Stats -->
    <div v-if="searchExecuted && stats.totalTasks > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Search Results Statistics</h3>

      <!-- Priority Filter Notice -->
      <div v-if="stats.filteredByPriority" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm text-blue-800">
          <FontAwesomeIcon :icon="['fas', 'filter']" class="mr-2" />
          Showing top {{ filters.topPriorityCount }} tasks by priority
          <span v-if="stats.priorityThreshold"> (minimum priority score: {{ stats.priorityThreshold }})</span>
        </p>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="text-2xl font-bold text-gray-900">{{ stats.totalTasks }}</div>
          <div class="text-sm text-gray-600">Total Tasks</div>
        </div>

        <div class="bg-blue-50 rounded-lg p-4">
          <div class="text-2xl font-bold text-blue-600">{{ stats.unassigned }}</div>
          <div class="text-sm text-gray-600">Unassigned</div>
        </div>

        <div class="bg-green-50 rounded-lg p-4">
          <div class="text-2xl font-bold text-green-600">{{ stats.active }}</div>
          <div class="text-sm text-gray-600">Active</div>
        </div>

        <div class="bg-purple-50 rounded-lg p-4">
          <div class="text-2xl font-bold text-purple-600">{{ stats.uniquePatients }}</div>
          <div class="text-sm text-gray-600">Unique Patients</div>
        </div>
      </div>

      <!-- Task Type Breakdown -->
      <div v-if="stats.byTaskType && Object.keys(stats.byTaskType).length > 0" class="mt-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3">By Task Type</h4>
        <div class="space-y-2">
          <div v-for="(count, typeName) in stats.byTaskType" :key="typeName" class="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
            <span class="text-sm text-gray-700">{{ typeName }}</span>
            <span class="text-sm font-semibold text-gray-900">{{ count }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Assignment -->
    <div v-if="searchExecuted && stats.totalTasks > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">
        Assign Tasks to User
      </h3>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Select User for Assignment
          </label>
          <select
            v-model="selectedUserId"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
          >
            <option value="">Choose a user...</option>
            <option v-for="user in users" :key="user.id" :value="user.id" class="sensitive">
              {{ user.last_name || 'Unknown' }}, {{ user.first_name || '' }} ({{ user.email || 'No email' }})
            </option>
          </select>
        </div>

        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div class="flex">
            <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" class="text-amber-600 mt-0.5" />
            <div class="ml-3">
              <p class="text-sm text-amber-800">
                This action will assign <strong>{{ stats.totalTasks }} tasks</strong> to the selected user.
                This operation cannot be easily undone.
              </p>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3">
          <button
            @click="resetSearch"
            class="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
          <button
            @click="assignTasks"
            :disabled="!selectedUserId || isAssigning"
            class="btn-primary"
          >
            <span v-if="!isAssigning" class="flex items-center gap-2">
              <FontAwesomeIcon :icon="['fas', 'user-plus']" />
              Assign Tasks
            </span>
            <span v-else class="flex items-center gap-2">
              <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
              Assigning...
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="searchExecuted && stats.totalTasks === 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
      <FontAwesomeIcon :icon="['fas', 'frown']" class="text-5xl text-gray-400 mb-4" />
      <h3 class="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
      <p class="mt-1 text-sm text-gray-500">Try adjusting your search filters</p>
      <button
        @click="resetSearch"
        class="btn-primary mt-4"
      >
        Start Over
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Reusable_Alert from '@/components/widgetcomponents/Reusable_Alert.vue';
import EntitySearch_V2 from "@/components/widgetcomponents/Search_Widgets/EntitySearch_V2.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'TaskAdminBulkAssignMode',
  components: {
    Reusable_Alert,
    EntitySearch_V2,
    FontAwesomeIcon,
    ComponentHeader
  },
  data() {
    return {
      // Entity selection
      selectedEntityIds: [],

      // Task filters
      filters: {
        taskType: '', // Now required
        payerName: '',
        topPriorityCount: null // New filter for priority
      },

      // Reference data
      taskTypes: [],
      users: [],

      // Found task IDs from search
      foundTaskIds: [],

      // Assignment
      selectedUserId: '',

      // State
      searchExecuted: false,
      isSearching: false,
      isAssigning: false,
      notifications: [],

      // Statistics
      stats: {
        totalTasks: 0,
        unassigned: 0,
        active: 0,
        uniquePatients: 0,
        byTaskType: {},
        filteredByPriority: false,
        priorityThreshold: null
      },

      // Search parameters for bulk assignment
      searchParams: null
    };
  },

  mounted() {
    this.loadInitialData();
  },

  methods: {
    async loadInitialData() {
      await Promise.all([
        this.fetchTaskTypes(),
        this.fetchUsers()
      ]);
    },

    handleEntitiesSelected(entityIds) {
      this.selectedEntityIds = entityIds;
      // Don't reset search automatically when entities change
    },

    clearEntitySelection() {
      this.selectedEntityIds = [];
    },

    showNotification(message, type = 'info') {
      const colorMap = {
        'error': '#FF968A',
        'success': '#90EE90',
        'warning': '#FFD700',
        'info': '#87CEEB'
      };

      this.notifications.push({
        message: message,
        color: colorMap[type] || colorMap.info
      });

      setTimeout(() => {
        if (this.notifications.length > 0) {
          this.notifications.shift();
        }
      }, 5000);
    },

    removeNotification(index) {
      this.notifications.splice(index, 1);
    },

    async fetchTaskTypes() {
      try {
        const response = await axios.get('/api/tasktypes/');
        this.taskTypes = response.data || [];
      } catch (error) {
        console.error('Error fetching task types:', error);
        this.taskTypes = [];
        this.showNotification('Failed to load task types', 'error');
      }
    },

    async fetchUsers() {
      try {
        const response = await axios.get('/api/user/');
        const userData = response.data || [];
        this.users = Array.isArray(userData) ? userData : [];

        this.users.sort((a, b) => {
          const lastNameA = a.last_name || '';
          const lastNameB = b.last_name || '';
          return lastNameA.localeCompare(lastNameB);
        });
      } catch (error) {
        console.error('Error fetching users:', error);
        this.users = [];
        this.showNotification('Failed to load users', 'error');
      }
    },

    async searchTasks() {
      if (!this.filters.taskType) {
        this.showNotification('Please select a task type', 'warning');
        return;
      }

      this.isSearching = true;
      this.searchExecuted = false;

      try {
        // Build request payload
        const payload = {
          task_type: this.filters.taskType,  // Required
          include_completed: false,
          unassigned_only: true  // NEW: Only get unassigned tasks for bulk assignment
        };

        // Add entity IDs only if selected
        if (this.selectedEntityIds.length > 0) {
          payload.entity_ids = this.selectedEntityIds;
        }

        // Add priority filter if specified
        if (this.filters.topPriorityCount && this.filters.topPriorityCount > 0) {
          payload.top_priority_count = this.filters.topPriorityCount;
        }

        // Add payer name filter if specified
        if (this.filters.payerName) {
          payload.payer_name = this.filters.payerName;
        }

        // Call the bulk-search endpoint
        const response = await axios.post('/api/tasks/bulk-search/', payload);

        if (response.data.success) {
          // Store task IDs for bulk assignment
          this.foundTaskIds = response.data.task_ids;

          // Store search params for bulk assignment
          this.searchParams = {
            task_type: this.filters.taskType  // Required
          };

          // Only add entity filter if entities were selected
          if (this.selectedEntityIds.length > 0) {
            this.searchParams.entity__in = this.selectedEntityIds.join(',');
          }

          if (this.filters.topPriorityCount && this.filters.topPriorityCount > 0) {
            this.searchParams.top_priority_count = this.filters.topPriorityCount;
          }

          if (this.filters.payerName) {
            this.searchParams['metadata__payer_name__icontains'] = this.filters.payerName;
          }

          // Update statistics from the summary
          const summary = response.data.summary;
          this.stats = {
            totalTasks: summary.total_count || 0,
            unassigned: summary.unassigned_count || 0,
            active: summary.active_count || 0,
            uniquePatients: summary.unique_patients || 0,
            byTaskType: summary.by_task_type || {},
            filteredByPriority: summary.filtered_by_priority || false,
            priorityThreshold: summary.priority_threshold || null
          };

          this.searchExecuted = true;

          if (this.stats.totalTasks === 0) {
            this.showNotification('No unassigned tasks found matching your criteria', 'info');
          } else {
            let message = `Found ${this.stats.totalTasks} unassigned tasks`;
            if (this.selectedEntityIds.length > 0) {
              message += ` across ${this.selectedEntityIds.length} entities`;
            } else {
              message += ` across all entities`;
            }
            if (this.stats.filteredByPriority) {
              message += ` (top ${this.filters.topPriorityCount} by priority)`;
            }
            this.showNotification(message, 'success');
          }
        } else {
          this.showNotification(response.data.message || 'Search failed', 'error');
        }
      } catch (error) {
        console.error('Error searching tasks:', error);
        this.showNotification('Failed to search tasks', 'error');
      } finally {
        this.isSearching = false;
      }
    },
    async assignTasks() {
      if (!this.selectedUserId) {
        this.showNotification('Please select a user', 'warning');
        return;
      }

      const selectedUser = this.users.find(u => u.id === this.selectedUserId);
      const userName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : 'the selected user';

      const confirmMessage = `Are you sure you want to assign ${this.stats.totalTasks} tasks to ${userName}?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      this.isAssigning = true;

      try {
        const response = await axios.post('/api/tasks/bulk-assign-owner/', {
          filters: this.searchParams,
          owner_id: this.selectedUserId
        });

        this.showNotification(
          `Successfully assigned ${response.data.assigned_count} tasks to ${userName}`,
          'success'
        );

        if (response.data.failed_count > 0) {
          this.showNotification(
            `${response.data.failed_count} tasks could not be assigned`,
            'warning'
          );
        }

        this.resetSearch();
      } catch (error) {
        console.error('Error assigning tasks:', error);
        this.showNotification('Failed to assign tasks', 'error');
      } finally {
        this.isAssigning = false;
      }
    },

    resetSearch() {
      this.selectedEntityIds = [];
      this.filters = {
        taskType: '',
        payerName: '',
        topPriorityCount: null
      };
      this.searchExecuted = false;
      this.selectedUserId = '';
      this.stats = {
        totalTasks: 0,
        unassigned: 0,
        active: 0,
        uniquePatients: 0,
        byTaskType: {},
        filteredByPriority: false,
        priorityThreshold: null
      };
      this.searchParams = null;
    }
  }
};
</script>

<style scoped>
/* Component styles */
</style>
